import type { ChallengeCheckpoint, CommunityChallenge } from '../types/engagement'

const DAY_MS = 86_400_000

function toISO(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function parseISODay(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d)
}

function dayKey(date: Date): number {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
}

/** Monday → Sunday of the week containing `on`. */
export function weekRange(on: Date = new Date()): { startDate: string; endDate: string } {
  const offset = (on.getDay() + 6) % 7
  const start = new Date(on.getFullYear(), on.getMonth(), on.getDate() - offset)
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6)
  return { startDate: toISO(start), endDate: toISO(end) }
}

export function monthRange(on: Date = new Date()): { startDate: string; endDate: string } {
  const start = new Date(on.getFullYear(), on.getMonth(), 1)
  const end = new Date(on.getFullYear(), on.getMonth() + 1, 0)
  return { startDate: toISO(start), endDate: toISO(end) }
}

export function yearRange(year: number = new Date().getFullYear()): { startDate: string; endDate: string } {
  return { startDate: `${year}-01-01`, endDate: `${year}-12-31` }
}

/** Suggested end date for a duration starting on `startDate` (Admin form helper). */
export function suggestedEndDate(duration: CommunityChallenge['duration'], startDate: string): string {
  const start = parseISODay(startDate)
  if (duration === 'weekly') return toISO(new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6))
  if (duration === 'monthly') return toISO(new Date(start.getFullYear(), start.getMonth() + 1, start.getDate() - 1))
  return toISO(new Date(start.getFullYear() + 1, start.getMonth(), start.getDate() - 1))
}

export interface ChallengeTimeline {
  totalDays: number
  elapsedDays: number
  remainingDays: number
  elapsedRatio: number
  status: 'upcoming' | 'active' | 'ended'
}

/** Elapsed and remaining period, inclusive of start and end days. */
export function challengeTimeline(
  challenge: Pick<CommunityChallenge, 'startDate' | 'endDate'>,
  today: Date = new Date()
): ChallengeTimeline {
  const start = dayKey(parseISODay(challenge.startDate))
  const end = dayKey(parseISODay(challenge.endDate))
  const now = dayKey(today)
  const totalDays = Math.round((end - start) / DAY_MS) + 1
  if (now < start) return { totalDays, elapsedDays: 0, remainingDays: totalDays, elapsedRatio: 0, status: 'upcoming' }
  if (now > end) return { totalDays, elapsedDays: totalDays, remainingDays: 0, elapsedRatio: 1, status: 'ended' }
  const elapsedDays = Math.round((now - start) / DAY_MS) + 1
  return {
    totalDays,
    elapsedDays,
    remainingDays: totalDays - elapsedDays,
    elapsedRatio: elapsedDays / totalDays,
    status: 'active',
  }
}

export type CheckpointState = 'reached' | 'below' | 'current' | 'upcoming'

/** Checkpoint state relative to today. "below" never implies elimination. */
export function checkpointState(
  checkpoint: ChallengeCheckpoint,
  year: number,
  today: Date = new Date()
): CheckpointState {
  const monthIndex = (today.getFullYear() - year) * 12 + today.getMonth()
  if (checkpoint.progress >= checkpoint.target) return 'reached'
  if (checkpoint.month < monthIndex) return 'below'
  if (checkpoint.month === monthIndex) return 'current'
  return 'upcoming'
}

/**
 * Sample helper: 12 monthly checkpoints with a per-month target and achieved values
 * for elapsed months (later months start at 0).
 */
export function monthlyCheckpoints(perMonth: number, achieved: number[]): ChallengeCheckpoint[] {
  return Array.from({ length: 12 }, (_, month) => ({
    month,
    target: perMonth,
    progress: achieved[month] ?? 0,
  }))
}

export function parseChallengeDate(iso: string): Date {
  return parseISODay(iso)
}
